import * as React from 'react';
import { useCallback, useContext } from 'react';

import { datadogRum } from '@datadog/browser-rum';
// eslint-disable-next-line no-restricted-imports, no-restricted-syntax
import { EventTrackingAction, TrackingOptions, useObservability } from '@opendoor/observability';
import { createBrowserHistory } from 'history';
import { merge } from 'lodash';

export const updateRUMGlobalContext = (context: { [k: string]: unknown }) => {
  datadogRum.setRumGlobalContext({ ...datadogRum.getRumGlobalContext(), ...context });
};

interface IMetricsContext {
  options?: unknown;
  trackEvent: (
    action: EventTrackingAction,
    label: string,
    data?: Record<string, unknown>,
    options?: TrackingOptions,
  ) => void;
  trackError: (
    label: string,
    error?: any,
    data?: Record<string, unknown>,
    options?: TrackingOptions,
  ) => void;
  trackPageView: (
    data?: Record<string, unknown> & { pathname?: string },
    options?: TrackingOptions,
  ) => void;
  updateContextualOptions: (newOptions: Record<string, unknown>) => void;
}

const MetricsContext = React.createContext<IMetricsContext>({
  trackEvent: () => {},
  trackError: () => {},
  trackPageView: () => {},
  updateContextualOptions: () => {},
});

export const useMetricsContext = () => useContext(MetricsContext);

export const MetricsContextProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { trackEvent, trackError } = useObservability();
  const [options, setOptions] = React.useState<Record<string, unknown>>({});

  /**
   * Merges the existing contextual data options with a new set of options.
   * The newly provided options will take precedence over the existing options.
   */
  const updateContextualOptions = useCallback(
    (newOptions: Record<string, unknown>) => {
      Object.keys(newOptions).forEach((key) => {
        if (Object.keys(options).includes(key)) {
          delete newOptions[key];
        }
      });

      if (Object.keys(newOptions).length > 0) {
        setOptions(merge(options, newOptions));
        updateRUMGlobalContext(newOptions);
      }
    },
    [options],
  );

  const trackEventWithContext = useCallback(
    (
      action: EventTrackingAction,
      label: string,
      data?: Record<string, unknown>,
      trackingOptions?: TrackingOptions,
    ) => trackEvent(action, label, data ? { ...options, ...data } : options, trackingOptions),
    [options, trackEvent],
  );

  const trackErrorWithContext = useCallback(
    (
      label: string,
      error?: Error,
      data?: Record<string, unknown>,
      trackingOptions?: TrackingOptions,
    ) => trackError(label, error, data ? { ...options, ...data } : options, trackingOptions),
    [options, trackError],
  );

  const trackPageViewWithContext = useCallback(
    (data?: Record<string, unknown> & { pathname?: string }, trackingOptions?: TrackingOptions) => {
      const history = createBrowserHistory();
      const pathname = history.location.pathname;
      const search = history.location.search;
      trackEvent(
        'view',
        'page',
        { resource: 'reception-fe', ...options, pathname, search, ...data },
        { categoryOverride: 'page', ...trackingOptions },
      );
    },
    [options, trackEvent],
  );

  React.useEffect(() => updateRUMGlobalContext(options), [options]);

  return (
    <MetricsContext.Provider
      value={{
        trackEvent: trackEventWithContext,
        trackError: trackErrorWithContext,
        trackPageView: trackPageViewWithContext,
        updateContextualOptions,
        options,
      }}
    >
      {children}
    </MetricsContext.Provider>
  );
};
